import { System } from '../../../engine/ecs/core/System';
import { Entity } from '../../../engine/ecs/core/Entity';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { SpriteComponent } from '../../../engine/ecs/components/SpriteComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';
import { EventManager } from '../../../engine/events/EventManager';
import { Event } from '../../../engine/events/Event';
import { geolocationService } from '../../../engine/utils/geolocation';

// Import Pacman-specific components
import { PacmanComponent } from '../components/PacmanComponent';
import { GhostComponent } from '../components/GhostComponent';

/**
 * Physics System for Pacman Physics Enhanced
 *
 * Features:
 * - Collision physics effects (particles, screen shake)
 * - Falling bricks from power pellets
 * - Physics-based visual enhancements
 * - Force-based movement effects
 */
export class PhysicsSystem extends System {
  private eventManager: EventManager;
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;

  // Physics effects
  private particles: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
    color: string;
    size: number;
    type: 'collision' | 'impact' | 'celebration' | 'death' | 'mud';
  }> = [];

  private screenShake: {
    x: number;
    y: number;
    duration: number;
    intensity: number;
  } = {
    x: 0,
    y: 0,
    duration: 0,
    intensity: 1,
  };

  private fallingBricks: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    width: number;
    height: number;
    active: boolean;
    rotation: number;
    rotationSpeed: number;
    type: 'mud_splash' | 'water_drop' | 'mud_cluster';
  }> = [];

  // Physics settings
  private gravity: number = 0.8;
  private friction: number = 0.95;
  private powerPelletActive: boolean = false;
  private powerPelletTimer: number = 0;

  // Enhanced effects
  private lastCollisionPosition: { x: number; y: number } | null = null;
  private explosionEffects: Array<{
    x: number;
    y: number;
    radius: number;
    maxRadius: number;
    life: number;
    maxLife: number;
    color: string;
  }> = [];

  constructor(canvas: HTMLCanvasElement) {
    super();
    this.canvas = canvas;
    this.context = canvas.getContext('2d')!;
    this.eventManager = EventManager.getInstance();

    this.requiredComponents = new Set([TransformComponent, SpriteComponent]);

    this.setupEventListeners();

    // Initialize geolocation detection for region-based content
    geolocationService.detectGermany().catch(console.warn);
  }

  private setupEventListeners(): void {
    this.eventManager.subscribe('ghost_eaten', this);
    this.eventManager.subscribe('pacman_died', this);
    this.eventManager.subscribe('power_pellet_collected', this);
    this.eventManager.subscribe('dot_collected', this);
    this.eventManager.subscribe('pacman_damaged', this);
    this.eventManager.subscribe('super_mode_activated', this);
    this.eventManager.subscribe('super_mode_deactivated', this);
  }

  public update(deltaTime: number): void {
    // Update particles
    this.updateParticles(deltaTime);

    // Update screen shake
    this.updateScreenShake(deltaTime);

    // Update falling bricks
    this.updateFallingBricks(deltaTime);

    // Update power pellet effects
    this.updatePowerPelletEffects(deltaTime);

    // Update explosion effects
    this.updateExplosionEffects(deltaTime);

    // Update super mode trailing effects
    this.updateSuperModeTrailingEffects(deltaTime);

    // Render physics effects
    this.renderPhysicsEffects();
  }

  private updateParticles(deltaTime: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];

      // Apply physics
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.vy += this.gravity * 0.2; // Stronger gravity for more dramatic effect
      particle.vx *= this.friction;
      particle.vy *= this.friction;

      // Reduce life
      particle.life -= deltaTime * 60;

      // Remove dead particles
      if (particle.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
  }

  private updateScreenShake(deltaTime: number): void {
    if (this.screenShake.duration > 0) {
      this.screenShake.duration -= deltaTime * 60;

      if (this.screenShake.duration > 0) {
        // More dramatic shake
        this.screenShake.x =
          (Math.random() - 0.5) * 8 * this.screenShake.intensity;
        this.screenShake.y =
          (Math.random() - 0.5) * 8 * this.screenShake.intensity;
      } else {
        this.screenShake.x = 0;
        this.screenShake.y = 0;
      }
    }
  }

  private updateFallingBricks(deltaTime: number): void {
    for (let i = this.fallingBricks.length - 1; i >= 0; i--) {
      const brick = this.fallingBricks[i];

      if (!brick.active) continue;

      // Apply gravity and rotation
      brick.vy += this.gravity;
      brick.x += brick.vx;
      brick.y += brick.vy;
      brick.rotation += brick.rotationSpeed;

      // Check if brick hit the ground
      if (brick.y > this.canvas.height) {
        brick.active = false;
        // Create mud impact particles instead of sand impact
        this.createMudImpactParticles(brick.x, brick.y, 15);
        this.addScreenShake(4, 1); // Reduced from (8, 2) for gentler mud effect
        this.createExplosionEffect(brick.x, brick.y, '#8B4513', 30);
      }

      // Check collision with Pacman
      this.checkBrickPacmanCollision(brick);
    }
  }

  private updatePowerPelletEffects(deltaTime: number): void {
    if (this.powerPelletActive) {
      this.powerPelletTimer -= deltaTime * 60;

      if (this.powerPelletTimer <= 0) {
        this.powerPelletActive = false;
        this.stopFallingBricks();
      }
    }
  }

  private updateExplosionEffects(deltaTime: number): void {
    for (let i = this.explosionEffects.length - 1; i >= 0; i--) {
      const explosion = this.explosionEffects[i];

      explosion.radius += 2;
      explosion.life -= deltaTime * 60;

      if (explosion.life <= 0) {
        this.explosionEffects.splice(i, 1);
      }
    }
  }

  private updateSuperModeTrailingEffects(deltaTime: number): void {
    // Find Pacman entity and check if it's in super mode
    const allEntities = this.entityManager.getAllEntities();

    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, PacmanComponent)) {
        const pacman = this.componentManager.getComponent(
          entity,
          PacmanComponent
        ) as PacmanComponent;
        const transform = this.componentManager.getComponent(
          entity,
          TransformComponent
        ) as TransformComponent;

        if (pacman.isSuperMode && pacman.isAlive) {
          // Create trailing effects around super mode Pacman
          this.createSuperModeTrailingEffects(
            transform.position.x,
            transform.position.y,
            pacman.currentDirection
          );
        }
      }
    }
  }

  private renderPhysicsEffects(): void {
    // Save context for screen shake
    this.context.save();
    this.context.translate(this.screenShake.x, this.screenShake.y);

    // Render explosion effects
    this.renderExplosionEffects();

    // Render particles
    this.renderParticles();

    // Render falling bricks
    this.renderFallingBricks();

    // Restore context
    this.context.restore();
  }

  private renderExplosionEffects(): void {
    for (const explosion of this.explosionEffects) {
      const alpha = explosion.life / explosion.maxLife;
      this.context.globalAlpha = alpha;

      // Create gradient for explosion
      const gradient = this.context.createRadialGradient(
        explosion.x,
        explosion.y,
        0,
        explosion.x,
        explosion.y,
        explosion.radius
      );
      gradient.addColorStop(0, explosion.color);
      gradient.addColorStop(0.5, 'rgba(255, 165, 0, 0.5)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      this.context.fillStyle = gradient;
      this.context.beginPath();
      this.context.arc(
        explosion.x,
        explosion.y,
        explosion.radius,
        0,
        Math.PI * 2
      );
      this.context.fill();
    }
    this.context.globalAlpha = 1;
  }

  private renderParticles(): void {
    for (const particle of this.particles) {
      const alpha = particle.life / particle.maxLife;
      this.context.globalAlpha = alpha;

      // Different rendering based on particle type
      switch (particle.type) {
        case 'collision':
          // Sparkle effect for collisions
          this.context.fillStyle = particle.color;
          this.context.beginPath();
          this.context.arc(
            particle.x,
            particle.y,
            particle.size * alpha,
            0,
            Math.PI * 2
          );
          this.context.fill();

          // Add glow effect
          this.context.shadowColor = particle.color;
          this.context.shadowBlur = 10;
          this.context.fill();
          this.context.shadowBlur = 0;
          break;

        case 'celebration':
          // Star effect for celebrations
          this.drawStar(
            particle.x,
            particle.y,
            particle.size * alpha,
            particle.color
          );
          break;

        case 'death':
          // Dark particles for death
          this.context.fillStyle = particle.color;
          this.context.beginPath();
          this.context.arc(
            particle.x,
            particle.y,
            particle.size * alpha * 1.5,
            0,
            Math.PI * 2
          );
          this.context.fill();
          break;

        case 'mud':
          // Render mud burst (muddy effect)
          this.context.save();
          this.context.beginPath();
          this.context.arc(
            particle.x,
            particle.y,
            particle.size * alpha * (0.8 + Math.random() * 0.4),
            0,
            Math.PI * 2
          );
          this.context.fillStyle = particle.color;
          this.context.shadowColor = '#8B4513';
          this.context.shadowBlur = 12;
          this.context.fill();
          this.context.shadowBlur = 0;
          this.context.restore();
          break;

        default:
          this.context.fillStyle = particle.color;
          this.context.beginPath();
          this.context.arc(
            particle.x,
            particle.y,
            particle.size * alpha,
            0,
            Math.PI * 2
          );
          this.context.fill();
      }
    }
    this.context.globalAlpha = 1;
  }

  private drawStar(x: number, y: number, size: number, color: string): void {
    this.context.fillStyle = color;
    this.context.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (i * 4 * Math.PI) / 5;
      const px = x + Math.cos(angle) * size;
      const py = y + Math.sin(angle) * size;
      if (i === 0) {
        this.context.moveTo(px, py);
      } else {
        this.context.lineTo(px, py);
      }
    }
    this.context.closePath();
    this.context.fill();
  }

  private renderFallingBricks(): void {
    for (const brick of this.fallingBricks) {
      if (!brick.active) continue;

      this.context.save();
      this.context.translate(brick.x, brick.y);
      this.context.rotate(brick.rotation);

      switch (brick.type) {
        case 'mud_splash':
          this.renderMudSplash(brick.width);
          break;
        case 'water_drop':
          this.renderWaterDrop(brick.width);
          break;
        case 'mud_cluster':
          this.renderMudCluster(brick.width);
          break;
      }

      this.context.restore();
    }
  }

  private renderMudSplash(size: number): void {
    this.context.fillStyle = '#8B4513';
    this.context.globalAlpha = 0.8;
    this.context.beginPath();
    this.context.arc(0, 0, size / 2, 0, Math.PI * 2);
    this.context.fill();
  }

  private renderWaterDrop(size: number): void {
    this.context.fillStyle = '#87CEEB';
    this.context.globalAlpha = 0.8;
    this.context.beginPath();
    this.context.arc(0, 0, size / 2, 0, Math.PI * 2);
    this.context.fill();
  }

  private renderMudCluster(size: number): void {
    const grad = this.context.createRadialGradient(0, 0, 0, 0, 0, size / 2);
    grad.addColorStop(0, '#A0522D');
    grad.addColorStop(0.7, '#8B4513');
    grad.addColorStop(1, '#654321');

    this.context.fillStyle = grad;
    this.context.beginPath();
    this.context.arc(0, 0, size / 2, 0, Math.PI * 2);
    this.context.fill();
  }

  private createCollisionParticles(
    x: number,
    y: number,
    color: string,
    count: number = 12
  ): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 30,
        y: y + (Math.random() - 0.5) * 30,
        vx: (Math.random() - 0.5) * 12,
        vy: (Math.random() - 0.5) * 12 - 3,
        life: 40 + Math.random() * 40,
        maxLife: 80,
        color: color,
        size: 3 + Math.random() * 4,
        type: 'collision',
      });
    }
  }

  private createCelebrationParticles(
    x: number,
    y: number,
    count: number = 20
  ): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 40,
        y: y + (Math.random() - 0.5) * 40,
        vx: (Math.random() - 0.5) * 15,
        vy: (Math.random() - 0.5) * 15 - 5,
        life: 60 + Math.random() * 60,
        maxLife: 120,
        color: ['#8B4513', '#A0522D', '#228B22', '#87CEEB'][
          Math.floor(Math.random() * 4)
        ],
        size: 4 + Math.random() * 6,
        type: 'celebration',
      });
    }
  }

  private createDeathParticles(x: number, y: number, count: number = 25): void {
    for (let i = 0; i < count; i++) {
      // TEMP: Force muddy colors for testing
      const isGermany = geolocationService.isGermanySync();
      let particleColor: string;

      if (isGermany) {
        // Germany-compliant colors (muddy theme - no blood colors)
        particleColor = [
          '#8B4513', // saddle brown
          '#A0522D', // sienna
          '#654321', // dark brown
          '#228B22', // forest green
          '#87CEEB', // sky blue
        ][Math.floor(Math.random() * 5)];
      } else {
        // Standard muddy colors for other regions
        particleColor = ['#8B4513', '#8B0000', '#DC143C', '#B22222'][
          Math.floor(Math.random() * 4)
        ];
      }

      this.particles.push({
        x: x + (Math.random() - 0.5) * 50,
        y: y + (Math.random() - 0.5) * 50,
        vx: (Math.random() - 0.5) * 18,
        vy: (Math.random() - 0.5) * 18 - 8,
        life: 80 + Math.random() * 80,
        maxLife: 160,
        color: particleColor,
        size: 6 + Math.random() * 10,
        type: 'mud',
      });
    }
  }

  private createImpactParticles(
    x: number,
    y: number,
    color: string,
    count: number = 8
  ): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 20,
        y: y,
        vx: (Math.random() - 0.5) * 8,
        vy: -Math.random() * 6 - 2,
        life: 30 + Math.random() * 30,
        maxLife: 60,
        color: color,
        size: 2 + Math.random() * 3,
        type: 'impact',
      });
    }
  }

  private createExplosionEffect(
    x: number,
    y: number,
    color: string,
    radius: number
  ): void {
    this.explosionEffects.push({
      x: x,
      y: y,
      radius: 5,
      maxRadius: radius,
      life: 30,
      maxLife: 30,
      color: color,
    });
  }

  private createFallingBricks(): void {
    // Create falling mud/water objects instead of mud bricks
    for (let i = 0; i < 8; i++) {
      const type = ['mud_splash', 'water_drop', 'mud_cluster'][
        Math.floor(Math.random() * 3)
      ] as 'mud_splash' | 'water_drop' | 'mud_cluster';
      const size =
        type === 'mud_splash'
          ? 8 + Math.random() * 8
          : type === 'water_drop'
          ? 6 + Math.random() * 12
          : 12 + Math.random() * 8;

      this.fallingBricks.push({
        x: Math.random() * this.canvas.width,
        y: -80 - Math.random() * 120,
        vx: (Math.random() - 0.5) * 4,
        vy: 0,
        width: size,
        height: type === 'water_drop' ? size * 2 : size,
        active: true,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.05, // Reduced from 0.2 for gentler rotation
        type: type,
      });
    }
  }

  private checkBrickPacmanCollision(brick: any): void {
    // Find Pacman entity
    for (const entity of this.entities) {
      const pacmanComponent = this.componentManager.getComponent(
        entity,
        PacmanComponent
      );
      if (!pacmanComponent) continue;

      const transform = this.componentManager.getComponent(
        entity,
        TransformComponent
      ) as TransformComponent;
      if (!transform) continue;

      // Simple collision detection
      const pacmanSize = 16;
      if (
        brick.x < transform.position.x + pacmanSize &&
        brick.x + brick.width > transform.position.x &&
        brick.y < transform.position.y + pacmanSize &&
        brick.y + brick.height > transform.position.y
      ) {
        // Collision! Deactivate brick and damage Pacman with snow effect
        brick.active = false;

        // Use same color logic as death particles for consistency
        const isGermany = geolocationService.isGermanySync();
        let collisionColor: string;
        if (isGermany) {
          // Germany-compliant muddy colors (no blood colors)
          collisionColor = '#8B4513'; // Saddle brown
        } else {
          // Standard muddy colors for other regions
          collisionColor = '#A0522D'; // Sienna
        }

        this.createCollisionParticles(
          transform.position.x,
          transform.position.y,
          collisionColor,
          20
        );
        this.addScreenShake(8, 1.5); // Reduced for gentler effect
        this.createExplosionEffect(
          transform.position.x,
          transform.position.y,
          isGermany ? '#8B4513' : '#A0522D', // Muddy vs sienna explosion
          40
        );

        // Emit damage event
        this.eventManager.publish(new Event('pacman_damaged'));
      }
    }
  }

  private addScreenShake(duration: number, intensity: number = 1): void {
    this.screenShake.duration = Math.max(this.screenShake.duration, duration);
    this.screenShake.intensity = Math.max(
      this.screenShake.intensity,
      intensity
    );
  }

  private stopFallingBricks(): void {
    for (const brick of this.fallingBricks) {
      brick.active = false;
    }
  }

  // Event handling
  public onEvent(event: Event): void {
    const eventType = event.getType();

    // Get position from event data or fallback to Pacman position
    let effectX = 100,
      effectY = 100;

    // Try to get position from event data first
    if ((event as any).position) {
      effectX = (event as any).position.x;
      effectY = (event as any).position.y;
    } else {
      // Fallback to Pacman position
      for (const entity of this.entities) {
        const pacmanComponent = this.componentManager.getComponent(
          entity,
          PacmanComponent
        );
        if (pacmanComponent) {
          const transform = this.componentManager.getComponent(
            entity,
            TransformComponent
          ) as TransformComponent;
          if (transform) {
            effectX = transform.position.x;
            effectY = transform.position.y;
            break;
          }
        }
      }
    }

    switch (eventType) {
      case 'ghost_eaten':
        // Check if this was a super mode ghost eating
        const isSuperModeKill = (event as any).isSuperModeKill;

        if (isSuperModeKill) {
          // DRAMATIC SUPER MODE GHOST EATING EFFECTS!
          this.createSuperModeGhostKillEffects(effectX, effectY, 120); // More particles
          this.addScreenShake(25, 8); // Much longer shake (8 seconds)
          this.createExplosionEffect(effectX, effectY, '#8B4513', 150); // Muddy brown explosion
          this.createExplosionEffect(effectX, effectY, '#A0522D', 100); // Sienna explosion
          this.createExplosionEffect(effectX, effectY, '#654321', 80); // Dark brown explosion
          this.createScreenFlash(); // Add dramatic screen flash
        } else {
          // Normal ghost eating effects
          this.createCollisionParticles(effectX, effectY, '#8B4513', 15);
          this.addScreenShake(8, 2);
        }
        break;

      case 'pacman_died':
        // Create mud burst instead of ice particles
        this.createDeathParticles(effectX, effectY, 35);
        this.addScreenShake(20, 4);
        this.createExplosionEffect(effectX, effectY, '#8B4513', 60); // Muddy explosion
        break;

      case 'power_pellet_collected':
        // Start falling mud/water effect instead of bricks
        this.powerPelletActive = true;
        this.powerPelletTimer = 300;
        this.createFallingBricks();
        this.createCelebrationParticles(effectX, effectY, 25);
        this.addScreenShake(6, 1.5); // Reduced from (12, 3) for gentler effect
        this.createExplosionEffect(effectX, effectY, '#8B4513', 45); // Muddy explosion
        break;

      case 'dot_collected':
        // Small particle effect for dots
        this.createCollisionParticles(effectX, effectY, '#8B4513', 5);
        break;

      case 'pacman_damaged':
        // Damage effect
        this.createCollisionParticles(effectX, effectY, '#8B4513', 15);
        this.addScreenShake(8, 2);
        break;

      case 'super_mode_activated':
        // Simple super mode activation effect
        this.createExplosionEffect(effectX, effectY, '#FFD700', 60); // Golden explosion
        this.addScreenShake(10, 2);
        break;

      case 'super_mode_deactivated':
        // Super mode deactivation effect
        this.createExplosionEffect(effectX, effectY, '#8B4513', 40); // Muddy explosion
        this.addScreenShake(8, 2);
        break;
    }
  }

  // Public API
  public getScreenShake(): { x: number; y: number } {
    return { x: this.screenShake.x, y: this.screenShake.y };
  }

  public isPowerPelletActive(): boolean {
    return this.powerPelletActive;
  }

  public getActiveBricksCount(): number {
    return this.fallingBricks.filter((brick) => brick.active).length;
  }

  private createMudImpactParticles(
    x: number,
    y: number,
    count: number = 8
  ): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 20,
        y: y,
        vx: (Math.random() - 0.5) * 8,
        vy: -Math.random() * 6 - 2,
        life: 30 + Math.random() * 30,
        maxLife: 60,
        color: ['#8B4513', '#A0522D', '#654321'][Math.floor(Math.random() * 3)],
        size: 2 + Math.random() * 3,
        type: 'impact',
      });
    }
  }

  /**
   * Create dramatic super mode ghost kill effects
   */
  private createSuperModeGhostKillEffects(
    x: number,
    y: number,
    count: number = 120
  ): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 150, // Bigger spread
        y: y + (Math.random() - 0.5) * 150, // Bigger spread
        vx: (Math.random() - 0.5) * 40, // Faster movement
        vy: (Math.random() - 0.5) * 40 - 15, // More upward movement
        life: 240 + Math.random() * 240, // Much longer life (4-8 seconds)
        maxLife: 480, // Double the max life
        color: [
          '#FF00FF', // Magenta
          '#FF6347', // Red
          '#00FF00', // Green
          '#FFD700', // Gold
          '#00FFFF', // Cyan
          '#FFA500', // Orange
          '#FF1493', // Deep pink
          '#32CD32', // Lime green
          '#FFFF00', // Yellow
        ][Math.floor(Math.random() * 9)],
        size: 10 + Math.random() * 15, // Bigger particles
        type: 'celebration',
      });
    }
  }

  /**
   * Create dramatic screen flash effect
   */
  private createScreenFlash(): void {
    // Create a temporary canvas overlay for the flash effect
    const flashCanvas = document.createElement('canvas');
    flashCanvas.style.position = 'absolute';
    flashCanvas.style.top = '0';
    flashCanvas.style.left = '0';
    flashCanvas.style.pointerEvents = 'none';
    flashCanvas.style.zIndex = '1000';
    flashCanvas.width = window.innerWidth;
    flashCanvas.height = window.innerHeight;

    const flashContext = flashCanvas.getContext('2d')!;

    // Add to DOM
    document.body.appendChild(flashCanvas);

    // Create flash animation
    let alpha = 0.8;
    const flashDuration = 800; // 800ms (much longer)
    const startTime = Date.now();

    const animateFlash = () => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / flashDuration;

      if (progress < 1) {
        alpha = 0.8 * (1 - progress);
        flashContext.clearRect(0, 0, flashCanvas.width, flashCanvas.height);
        flashContext.fillStyle = `rgba(139, 69, 19, ${alpha})`; // Muddy brown flash
        flashContext.fillRect(0, 0, flashCanvas.width, flashCanvas.height);
        requestAnimationFrame(animateFlash);
      } else {
        // Remove flash canvas
        document.body.removeChild(flashCanvas);
      }
    };

    animateFlash();
  }

  /**
   * Create minor trailing effects around super mode Pacman
   */
  private createSuperModeTrailingEffects(
    x: number,
    y: number,
    direction: string
  ): void {
    // Create small trailing particles behind Pacman
    for (let i = 0; i < 3; i++) {
      const offsetX = (Math.random() - 0.5) * 20;
      const offsetY = (Math.random() - 0.5) * 20;

      this.particles.push({
        x: x + offsetX,
        y: y + offsetY,
        vx: (Math.random() - 0.5) * 3, // Slow movement
        vy: (Math.random() - 0.5) * 3 - 1, // Slight upward drift
        life: 30 + Math.random() * 30, // Short life
        maxLife: 60,
        color: [
          '#FFD700', // Gold
          '#FFA500', // Orange
          '#FF6347', // Red
        ][Math.floor(Math.random() * 3)],
        size: 2 + Math.random() * 3, // Small particles
        type: 'celebration',
      });
    }

    // Add occasional sparkle effects
    if (Math.random() < 0.3) {
      // 30% chance each frame
      this.particles.push({
        x: x + (Math.random() - 0.5) * 30,
        y: y + (Math.random() - 0.5) * 30,
        vx: (Math.random() - 0.5) * 5,
        vy: (Math.random() - 0.5) * 5 - 2,
        life: 20 + Math.random() * 20,
        maxLife: 40,
        color: '#FFFF00', // Bright yellow sparkles
        size: 1 + Math.random() * 2,
        type: 'celebration',
      });
    }
  }
}
